import React from "react";
import useSeo from "../hooks/useSeo";
import { Calendar, AlertCircle } from "lucide-react";

const HOLIDAYS_2026 = [
  { date: "2026-01-01", day: "Torsdag", name: "Nytårsdag", note: "Lukket" },
  { date: "2026-04-02", day: "Torsdag", name: "Skærtorsdag", note: "Lukket" },
  { date: "2026-04-03", day: "Fredag", name: "Langfredag", note: "Lukket" },
  { date: "2026-04-05", day: "Søndag", name: "Påskedag", note: "Lukket" },
  { date: "2026-04-06", day: "Mandag", name: "2. påskedag", note: "Lukket" },
  { date: "2026-05-01", day: "Fredag", name: "1. maj", note: "Reduceret bemanding" },
  { date: "2026-05-14", day: "Torsdag", name: "Kristi himmelfartsdag", note: "Lukket" },
  { date: "2026-05-15", day: "Fredag", name: "Dagen efter Kristi himmelfart", note: "Lukket" },
  { date: "2026-05-24", day: "Søndag", name: "Pinsedag", note: "Lukket" },
  { date: "2026-05-25", day: "Mandag", name: "2. pinsedag", note: "Lukket" },
  { date: "2026-06-05", day: "Fredag", name: "Grundlovsdag", note: "Reduceret bemanding (lukket fra kl. 12)" },
  { date: "2026-12-24", day: "Torsdag", name: "Juleaftensdag", note: "Lukket fra kl. 12" },
  { date: "2026-12-25", day: "Fredag", name: "Juledag", note: "Lukket" },
  { date: "2026-12-26", day: "Lørdag", name: "2. juledag", note: "Lukket" },
  { date: "2026-12-31", day: "Torsdag", name: "Nytårsaftensdag", note: "Lukket fra kl. 14" },
];

export default function ClosedDaysPage() {
  useSeo({
    title: "Lukkedage 2026 · EMA Kurer ApS",
    description:
      "Oversigt over EMA Kurers lukkedage og helligdage i 2026 — så du kan planlægge dine kurérleveringer i god tid.",
    path: "/lukkedage",
  });

  return (
    <div className="bg-brand-bg2 min-h-screen" data-testid="closed-days-page">
      {/* Hero */}
      <section className="bg-brand-dark text-white py-16">
        <div className="max-w-4xl mx-auto px-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-brand-red/20 text-brand-red px-3 py-1.5 rounded-full mb-4">
            <Calendar size={14} /> Lukkedage 2026
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight">
            Lukkedage & helligdage
          </h1>
          <p className="mt-4 text-lg text-white/80">
            Vi kører kurer-leveringer alle hverdage og weekender — dog med følgende lukkedage i 2026.
          </p>
        </div>
      </section>

      {/* Holiday table */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-5">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-display text-lg font-bold text-brand-dark">2026 — Officielle lukkedage</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="holiday-table">
                <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4">Dato</th>
                    <th className="py-3 px-4">Ugedag</th>
                    <th className="py-3 px-4">Helligdag</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {HOLIDAYS_2026.map((h) => (
                    <tr key={h.date} className="border-b border-slate-100 last:border-b-0" data-testid={`holiday-${h.date}`}>
                      <td className="py-3 px-4 font-mono text-xs">{h.date}</td>
                      <td className="py-3 px-4 text-brand-text2">{h.day}</td>
                      <td className="py-3 px-4 font-semibold text-brand-dark">{h.name}</td>
                      <td className="py-3 px-4">
                        {h.note === "Lukket" ? (
                          <span className="inline-block rounded-md bg-red-100 text-red-800 px-2 py-0.5 text-xs">Lukket</span>
                        ) : (
                          <span className="inline-block rounded-md bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">{h.note}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Info */}
          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-700 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-lg font-bold text-brand-dark mb-2">Vigtigt at vide</h3>
                <ul className="space-y-1.5 text-sm text-brand-text2">
                  <li>• Bookinger på lukkedage flyttes automatisk til næste åbne hverdag</li>
                  <li>• Erhvervskunder med fast aftale får besked via email mindst 7 dage før hver lukkedag</li>
                  <li>• Akutte erhvervs-leveringer på lukkedage kan aftales — kontakt <a href="mailto:akut@emakurer.dk" className="text-brand-red font-semibold">akut@emakurer.dk</a> (særtillæg +75%)</li>
                  <li>• Live-tracking og kundeservice er begrænset på lukkedage</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Normal hours */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-brand-dark mb-3">Almindelige åbningstider</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="font-semibold text-brand-dark">Hverdage</div>
                <div className="text-brand-text2">08:00 – 22:00</div>
              </div>
              <div>
                <div className="font-semibold text-brand-dark">Lørdag</div>
                <div className="text-brand-text2">08:00 – 20:00</div>
              </div>
              <div>
                <div className="font-semibold text-brand-dark">Søndag</div>
                <div className="text-brand-text2">10:00 – 18:00</div>
              </div>
            </div>
            <p className="text-xs text-brand-text2 mt-3">
              Online-bookinger kan oprettes 24/7. Afhentning sker inden for åbningstid og dit valgte tidsvindue.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
